import React from 'react';
import { useTranslation } from 'react-i18next';
import { ScanText, PenLine } from 'lucide-react';
import { BulkUploadMethod } from '../../../../api/General/bulkUpload';

interface UploadMethodStepProps {
  method: BulkUploadMethod | null;
  onMethodSelect: (method: BulkUploadMethod) => void;
}

const UploadMethodStep: React.FC<UploadMethodStepProps> = ({ method, onMethodSelect }) => {
  const { t } = useTranslation('common');

  return (
    <div className="flex flex-col items-center justify-center gap-8 py-12 px-6">
      <div className="text-center max-w-lg">
        <h2 className="text-xl font-semibold text-foreground mb-2">
          {t('Choose Upload Method')}
        </h2>
        <p className="text-sm text-muted-foreground">
          {t('Select how you want to process the uploaded documents')}
        </p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 w-full max-w-xl">
        {/* OCR Option */}
        <button
          type="button"
          onClick={() => onMethodSelect(BulkUploadMethod.OCR)}
          className={`
            group relative flex flex-col items-center gap-4 p-8 rounded-xl border-2 transition-all cursor-pointer
            ${method === BulkUploadMethod.OCR
              ? 'border-primary bg-primary/5 ring-2 ring-primary/20'
              : 'border-border hover:border-primary/50 hover:bg-muted/50'
            }
          `}
        >
          <div className={`
            flex items-center justify-center w-14 h-14 rounded-xl transition-colors
            ${method === BulkUploadMethod.OCR
              ? 'bg-primary text-primary-foreground'
              : 'bg-muted text-muted-foreground group-hover:bg-primary/10 group-hover:text-primary'
            }
          `}>
            <ScanText className="w-7 h-7" />
          </div>
          <div className="text-center">
            <p className="text-base font-semibold text-foreground mb-1">
              {t('OCR Auto-Detect')}
            </p>
            <p className="text-xs text-muted-foreground leading-relaxed">
              {t('Automatically detect employee names and document types from file content')}
            </p>
          </div>
          {method === BulkUploadMethod.OCR && (
            <div className="absolute top-3 end-3 w-5 h-5 rounded-full bg-primary flex items-center justify-center">
              <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
              </svg>
            </div>
          )}
        </button>

        {/* Manual Option */}
        <button
          type="button"
          onClick={() => onMethodSelect(BulkUploadMethod.Manual)}
          className={`
            group relative flex flex-col items-center gap-4 p-8 rounded-xl border-2 transition-all cursor-pointer
            ${method === BulkUploadMethod.Manual
              ? 'border-primary bg-primary/5 ring-2 ring-primary/20'
              : 'border-border hover:border-primary/50 hover:bg-muted/50'
            }
          `}
        >
          <div className={`
            flex items-center justify-center w-14 h-14 rounded-xl transition-colors
            ${method === BulkUploadMethod.Manual
              ? 'bg-primary text-primary-foreground'
              : 'bg-muted text-muted-foreground group-hover:bg-primary/10 group-hover:text-primary'
            }
          `}>
            <PenLine className="w-7 h-7" />
          </div>
          <div className="text-center">
            <p className="text-base font-semibold text-foreground mb-1">
              {t('Manual Mapping')}
            </p>
            <p className="text-xs text-muted-foreground leading-relaxed">
              {t('Manually assign employees and document types to each uploaded file')}
            </p>
          </div>
          {method === BulkUploadMethod.Manual && (
            <div className="absolute top-3 end-3 w-5 h-5 rounded-full bg-primary flex items-center justify-center">
              <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
              </svg>
            </div>
          )}
        </button>
      </div>
    </div>
  );
};

export default UploadMethodStep;
